import { useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { deleteGroup } from "../groupsApi";
import { InfoBanner } from "../groupsUi";

/**
 * Maps backend error strings to friendly French messages.
 * @param {string} raw - Server message
 * @returns {string}
 */
function mapDeleteError(raw) {
    if (typeof raw !== "string") return "Échec de la suppression";
    if (raw.includes("event in progress")) return "Suppression bloquée : un événement est en cours pour ce groupe.";
    if (raw.includes("payment settlement in progress")) return "Suppression bloquée : un paiement de réservation est en cours de règlement (automatique au plus tard 48h après l'événement).";
    if (raw.includes("Please transfer funds")) return "Suppression bloquée : le solde du wallet doit être à zéro avant suppression.";
    return raw;
}

/**
 * Submits the deletion and surfaces toasts.
 * @param {Object} args - Submission args
 */
async function submitDelete({ groupId, setLoading, onClose, onDeleted }) {
    setLoading(true);
    try {
        await deleteGroup(groupId);
        toast.success("Groupe supprimé");
        onDeleted?.();
        onClose();
    } catch (error) {
        const msg = error?.response?.data;
        toast.error(mapDeleteError(msg));
    } finally {
        setLoading(false);
    }
}

/**
 * Body of the deletion confirmation.
 * @param {Object} props - { group }
 */
function DialogBody({ group }) {
    const textColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const balance = Number(group?.wallets?.[0]?.balance ?? 0);
    return (
        <Stack spacing="16px">
            <InfoBanner tone="danger">
                Action irréversible. Le groupe, ses contacts et ses ressources seront supprimés définitivement.
            </InfoBanner>
            <Text color={textColor} fontSize="0.85rem">
                Vous êtes sur le point de supprimer le groupe <strong>{group?.name || "—"}</strong>.
            </Text>
            <Text color={subColor} fontSize="0.78rem">
                Pré-requis : aucun événement en cours, aucun paiement en attente de règlement et solde wallet à zéro (actuel : {balance.toFixed(2)}).
            </Text>
        </Stack>
    );
}

/**
 * Confirmation dialog for deleting a group.
 * @param {Object} props - { open, group, onClose, onDeleted }
 */
function DeleteGroupDialog({ open, group, onClose, onDeleted }) {
    const [loading, setLoading] = useState(false);
    const onSubmit = () => submitDelete({ groupId: group?.id, setLoading, onClose, onDeleted });

    return (
        <Modal isOpen={open} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Supprimer le groupe</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody group={group} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="red" onClick={onSubmit} isLoading={loading} loadingText="Suppression...">Supprimer définitivement</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default DeleteGroupDialog;
